import React, { useEffect, useMemo, useState } from 'react';

import useDesktopObservation from '@/hooks/useDesktopObservation';
import useDesktopCommand from '@/hooks/useDesktopCommand';
import { useServicesUnavailable } from './DeviceStatusSlot';
import KeptRangesTimeline from './KeptRangesTimeline';
import { Database, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RangeCalendar from '@/components/RangeCalendar';

function iso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function bytes(value) {
  if (!Number.isFinite(value) || value < 0) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KiB`;
  return `${(value / 1024 ** 2).toFixed(1)} MiB`;
}

function failureMessage(reason) {
  if (reason === 'historical_recovery_confirmation_required') {
    return 'This refresh needs a new confirmation. Use Download this range again to review a fresh copy; Retry will only check its save receipt.';
  }
  if (reason === 'historical_receipt_observation_invalid') {
    return 'The save receipt check could not be read safely. No fresh download was started.';
  }
  if (reason === 'recovery_state_changed') {
    return 'The saved data changed during preparation. Check the range again before confirming a fresh copy.';
  }
  if (reason === 'historical_recovery_pending' || reason === 'historical_attempt_superseded') {
    return 'An earlier attempt was replaced by a confirmed refresh. Check its receipt, or prepare a new fresh copy if it remains unresolved.';
  }
  if (reason === 'recovery_history_full') {
    return 'The recovery record has reached its safe limit. Contact support; earlier evidence has been kept.';
  }
  if (reason === 'historical_commit_outcome_uncertain') {
    return 'This computer could not confirm whether the range was saved. Retry checks the save receipt first; it will not download the range again unless a safe outcome is known.';
  }
  if (reason === 'historical_transaction_aborted') {
    return 'The range was not saved. Existing data was preserved. You can retry this range.';
  }
  // TCA-26100809: a failed response identifies a step, not corrupt order data.
  if (typeof reason === 'string' && /^historical_(cloud_package|media_summary|local_apply)_(response_invalid_json|error_response_invalid_json|response_too_large|request_timeout|request_failed|http_[45]\d\d)$/.test(reason)) {
    if (reason.startsWith('historical_local_apply_')) {
      return 'The local saving step could not confirm its result. Contact support before retrying this range.';
    }
    return reason.startsWith('historical_media_summary_')
      ? 'The local media check did not finish. Support details identify the failed request.'
      : 'The online historical download did not finish. Support details identify the failed request.';
  }
  if (['cloud_session_required', 'active_administrator_required', 'cloud_session_changed'].includes(reason)) {
    return 'Sign in online as an administrator, then try again.';
  }
  if (reason === 'order_data_operation_in_progress' || reason === 'historical_operation_in_progress') {
    return 'Another offline-data task is running. Try again when it finishes.';
  }
  if (reason === 'historical_response_too_large') {
    return 'The cloud response was larger than this feature can safely accept.';
  }
  if (reason === 'historical_response_invalid_json') {
    return 'A historical-data response could not be read. The saved report does not identify which step failed.';
  }
  if (reason === 'historical_package_invalid') {
    return 'The downloaded historical package could not be safely verified.';
  }
  if (reason === 'local_control_unavailable') {
    return 'Protected offline services were not ready. Restart the app and try again.';
  }
  if (reason === 'historical_media_estimate_unavailable') {
    return 'The app could not verify the media needed for this range.';
  }
  if (reason === 'safe_storage_unavailable' || reason === 'registry_invalid'
      || reason === 'registry_too_large') {
    return 'The protected historical-data record is unavailable and needs administrator attention.';
  }
  return 'This range could not be restored. Check the connection and try again.';
}

export default function HistoricalOfflineDataCard({ onSummary = null }) {
  const observation = useDesktopObservation('getHistoricalOrderDataStatus', 'onHistoricalOrderDataStatusChanged');
  const command = useDesktopCommand('order-data');
  const bridge = command.scope.bridge;
  const servicesUnavailable = useServicesUnavailable();
  const status = observation.status || { state: bridge ? (observation.loading ? 'checking' : 'status_unavailable') : 'desktop_required' };
  const [failure, setFailure] = useState(null);
  const [range, setRange] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    setRange(null); setCalendarOpen(false); setFailure(null);
  }, [command.scope]);

  const selected = useMemo(() => {
    if (!range?.from || !range?.to) return null;
    return { dateFrom: iso(range.from), dateTo: iso(range.to) };
  }, [range]);
  const busy = command.busy || ['preparing', 'applying', 'replaying', 'cancelling'].includes(status.state);
  const eligible = command.scope.admin && command.scope.cloud && !servicesUnavailable && observation.phase === 'ready';
  const awaiting = status.state === 'awaiting_confirmation';
  const retained = Array.isArray(status.retainedRanges) ? status.retainedRanges : [];
  const available = retained.filter((item) => item.state === 'completed');
  const failed = retained.filter((item) => item.state === 'failed');
  const pending = retained.filter((item) => item.state === 'confirmed');
  const reconfirmationRequired = retained.filter(
    (item) => item.state === 'reconfirmation_required',
  );
  const retainedOrderCount = available.reduce((total, item) => total + (item.orderCount || 0), 0);
  const retainedMediaBytes = available.reduce(
    (total, item) => total + (item.eligibleMediaBytes || 0),
    0,
  );
  const oldestRetainedDate = available.reduce(
    (oldest, item) => (!oldest || item.dateFrom < oldest ? item.dateFrom : oldest),
    null,
  );

  // Report what is already read here so the tab can summarise kept ranges
  // truthfully. A second reader would duplicate this subscription, and an
  // unread count must never be presented as a confirmed zero (TCA-26100804).
  const statusKnown = observation.phase === 'ready' && Array.isArray(status.retainedRanges);
  useEffect(() => {
    if (!onSummary) return;
    onSummary(statusKnown
      ? { rangeCount: available.length, orderCount: retainedOrderCount, oldest: oldestRetainedDate }
      : null);
    return () => onSummary(null);
  }, [onSummary, statusKnown, available.length, retainedOrderCount, oldestRetainedDate]);

  const invoke = async (method, payload) => {
    if ((method === 'cancelHistoricalOrderData' ? !command.scope.admin : !eligible) || busy || !observation.current() || !command.scope.current() || typeof bridge?.[method] !== 'function') return;
    const accept = observation.capture();
    setFailure(null);
    await command.run(native => payload === undefined ? native[method]() : native[method](payload),
      async (result, error) => {
        if (error || !result || typeof result.state !== 'string') {
          setFailure('The range outcome could not be confirmed. Check its native receipt before another attempt.');
          await observation.refresh();
          return;
        }
        // A later native event owns the status. A command cannot overwrite it.
        accept({ ...status, ...result });
        if (result.ok === false) setFailure(failureMessage(result.reason));
      });
  };
  const prepare = () => {
    if (selected && !awaiting) return invoke('prepareHistoricalOrderData', selected);
  };
  const confirm = () => {
    if (awaiting && status.summary) return invoke('confirmHistoricalOrderData');
  };
  const cancel = () => {
    if (awaiting) return invoke('cancelHistoricalOrderData');
  };
  const reconfirm = () => invoke('reconfirmHistoricalOrderData');
  const retryFailed = () => { if (!awaiting) return invoke('retryFailedHistoricalOrderData'); };
  const prepareRecovery = item => {
    if (!awaiting && failed.includes(item)) return invoke('prepareHistoricalRecovery', { dateFrom: item.dateFrom, dateTo: item.dateTo });
  };

  if (!bridge) return null;
  const summary = status.summary;
  return (
    <section className="rounded-xl border border-stone-200 bg-white px-4 py-4" data-testid="historical-offline-data-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-emerald-700" />
            <h3 className="text-sm font-semibold text-stone-800">Historical offline data</h3>
          </div>
          <p className="mt-1 text-xs text-stone-500">
            Keep an older continuous delivery-date range on this computer. Each request may cover up to 62 days.
          </p>
        </div>
        <span className="rounded-full bg-stone-100 px-2 py-1 text-xs" data-testid="historical-status">
          {status.state.replaceAll('_', ' ')}
        </span>
      </div>

      {(failure || command.uncertain) && <p role="status">{failure || 'The previous range outcome is unknown. Check native recovery or contact support before repeating it.'}</p>}
      {observation.phase === 'unavailable' && <p role="status">Historical status could not be read. Kept ranges are unknown.</p>}
      {!awaiting && (
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={() => setCalendarOpen((value) => !value)} data-testid="historical-range-open">
            {selected ? `${selected.dateFrom} – ${selected.dateTo}` : 'Choose date range'}
          </Button>
          {calendarOpen && (
            <div className="mt-2 w-fit rounded-lg border border-stone-200 p-2" data-testid="historical-range-calendar">
              <RangeCalendar value={range} onChange={setRange} numberOfMonths={2} maxSpanDays={62} />
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={prepare} disabled={!selected || busy || !eligible} data-testid="historical-prepare">
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {busy ? 'Checking…' : 'Check availability'}
            </Button>
          </div>
        </div>
      )}

      {awaiting && summary && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="historical-confirmation">
          <p className="text-sm font-semibold text-stone-800">
            {summary.orderCount.toLocaleString()} orders from {summary.dateFrom} through {summary.dateTo}
          </p>
          <p className="mt-1 text-xs text-stone-600">
            Eligible offline media: {summary.media.eligible.count.toLocaleString()} items ({bytes(summary.media.eligible.bytes)}).
            {' '}Excluded by policy: {summary.media.excluded.count.toLocaleString()} items ({bytes(summary.media.excluded.bytes)}).
          </p>
          <p className="mt-2 text-xs text-amber-900">
            Orders are saved first. Signatures and still-eligible source documents download afterward. Photos, videos and expired source documents are not added for historical use. Structured customer and order history remains on this installation.
          </p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={confirm} disabled={busy || !eligible} data-testid="historical-confirm">{summary.recovery ? 'Download fresh copy' : 'Download range'}</Button>
            <Button variant="outline" size="sm" onClick={cancel} disabled={busy || !command.scope.admin} data-testid="historical-cancel">
              <X className="mr-1 h-3.5 w-3.5" />Cancel
            </Button>
          </div>
          {summary.recovery && (
            <p className="mt-2 text-xs text-amber-900" data-testid="historical-recovery-warning">
              We could not confirm the earlier save. This downloads a fresh copy of this range. Your unsent offline work will be kept. Other kept ranges will not be downloaded again.
            </p>
          )}
        </div>
      )}

      {status.state === 'completed' && failed.length === 0 && pending.length === 0
        && reconfirmationRequired.length === 0 && (
        <p className="mt-3 text-xs text-emerald-700" data-testid="historical-completed">
          Historical orders are available offline. Eligible media will continue converging through Offline media.
        </p>
        )}
      {reconfirmationRequired.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="historical-reconfirmation">
          <p className="text-xs text-amber-900">
            Protected storage was rebuilt. Restore {reconfirmationRequired.length.toLocaleString()} previously requested historical {reconfirmationRequired.length === 1 ? 'range' : 'ranges'} after the normal offline baseline.
          </p>
          <Button className="mt-2" size="sm" onClick={reconfirm} disabled={busy || !eligible} data-testid="historical-reconfirm">
            Restore retained ranges
          </Button>
        </div>
      )}
      {failed.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="historical-failed-ranges">
          <p className="text-sm font-semibold text-red-900">
            Historical ranges need attention ({failed.length})
          </p>
          <p className="mt-1 text-xs text-red-800">
            These ranges are not currently counted as available offline. Successful ranges will not be downloaded again.
          </p>
          <div className="mt-2 space-y-2">
            {failed.map((item) => (
              <div key={`${item.dateFrom}:${item.dateTo}`} className="text-xs text-red-900">
                <p className="font-medium">{item.dateFrom} – {item.dateTo}</p>
                <p>{failureMessage(item.lastReason)}</p>
                <p className="text-red-700">Reason: {item.lastReason || 'historical_operation_failed'}</p>
                <Button className="mt-2" variant="outline" size="sm" disabled={busy || awaiting || !eligible || !bridge.prepareHistoricalRecovery}
                  onClick={() => prepareRecovery(item)}>
                  Download this range again
                </Button>
              </div>
            ))}
          </div>
          <Button className="mt-3" size="sm" onClick={retryFailed} disabled={busy || awaiting || !eligible} data-testid="historical-retry-failed">
            {status.state === 'replaying' ? 'Retrying…' : 'Retry failed ranges'}
          </Button>
          {['failed', 'rejected', 'session_required', 'registry_unavailable'].includes(status.state) && status.reason && (
            <p className="mt-2 text-xs text-red-800" data-testid="historical-retry-error">
              {failureMessage(status.reason)}
            </p>
          )}
        </div>
      )}
      {pending.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="historical-pending-ranges">
          <p className="text-xs font-semibold text-amber-900">
            Restoration pending ({pending.length})
          </p>
          <div className="mt-1 space-y-1 text-xs text-amber-800">
            {pending.map((item) => (
              <p key={`${item.dateFrom}:${item.dateTo}`}>{item.dateFrom} – {item.dateTo}</p>
            ))}
          </div>
        </div>
      )}
      {available.length > 0 && (
        <div className="mt-3 border-t border-stone-200 pt-3" data-testid="historical-retained-ranges">
          <p className="text-xs font-semibold text-stone-700">
            Available ranges ({available.length}) · {retainedOrderCount.toLocaleString()} orders · {bytes(retainedMediaBytes)} eligible media · oldest {oldestRetainedDate}
          </p>
          {/* TCA-26100804: the ISO-date list was hard to read; same data, grouped. */}
          <div className="mt-2">
            <KeptRangesTimeline ranges={available} />
          </div>
        </div>
      )}
      {['failed', 'rejected', 'session_required', 'registry_unavailable'].includes(status.state)
        && failed.length === 0 && (
        <p className="mt-3 text-xs text-red-700" data-testid="historical-error">
          Historical data could not be prepared: {status.reason || 'status unavailable'}.
        </p>
        )}
    </section>
  );
}
